import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import { supabase } from '../../config/supabase';
import MainHeader from '../../components/MainHeader';
import Footer from '../../components/Footer';
import './MainScreens.css';

export default function VisitConfirmationScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const { sessionId, restaurant } = location.state || {};
  
  const [photoUrl, setPhotoUrl] = useState('');
  const [billAmount, setBillAmount] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [locationVerified, setLocationVerified] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [monthlyRemaining, setMonthlyRemaining] = useState(null);
  const [orderType, setOrderType] = useState('dine_in');
  const [deliveryPlatform, setDeliveryPlatform] = useState('');

  useEffect(() => {
    if (!sessionId || !restaurant) {
      navigate('/home');
      return;
    }

    // Get monthly limit status
    async function checkMonthlyLimit() {
      try {
        const { data } = await api.get('/cashback/balance');
        setMonthlyRemaining(data.monthly_remaining);
        
        if (data.monthly_remaining === 0) {
          setError(`Monthly cashback limit reached (${data.monthly_limit} visits/month). Resets next month.`);
        }
      } catch (err) {
        console.warn('Failed to check monthly limit:', err);
      }
    }

    checkMonthlyLimit();

    // Get user's current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
          setLocationVerified(true);
        },
        (err) => {
          console.warn('Location error:', err);
          setError('Location access required for verification');
        }
      );
    }
  }, [sessionId, restaurant, navigate]);

  async function handlePhotoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('Photo must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      // Upload the receipt/order-confirmation image to Supabase Storage
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: upErr } = await supabase.storage.from('receipts').upload(path, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type || 'image/jpeg',
      });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from('receipts').getPublicUrl(path);
      setPhotoUrl(pub.publicUrl);
    } catch (err) {
      console.error('Receipt upload error:', err);
      setError('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit() {
    if (!photoUrl) {
      setError('Please upload a receipt or food photo');
      return;
    }

    if (!billAmount || parseFloat(billAmount) < 10) {
      setError('Please enter bill amount ($10 minimum for cashback)');
      return;
    }

    if (!locationVerified) {
      setError('Location verification required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const { data } = await api.post('/visits/verify', {
        sessionId,
        restaurantPlaceId: restaurant.placeId || restaurant.id,
        restaurantName: restaurant.name,
        photoUrl,
        billAmount: parseFloat(billAmount),
        location: userLocation,
        orderType,
        deliveryPlatform: orderType === 'delivery' ? deliveryPlatform : null,
      });

      // Show success message
      const { verification, message, remainingThisMonth } = data;
      
      let alertMessage = message;
      alertMessage += `\n\n🎯 ${remainingThisMonth} cashback visit${remainingThisMonth === 1 ? '' : 's'} remaining this month`;
      
      alert(alertMessage);
      navigate('/rewards');
    } catch (err) {
      if (err.response?.data?.code === 'PREMIUM_REQUIRED') {
        setError('Premium subscription required to earn cashback. Upgrade to Premium!');
      } else if (err.response?.data?.code === 'MONTHLY_LIMIT_REACHED') {
        setError(err.response.data.error);
      } else {
        setError(err.response?.data?.error || 'Failed to verify visit');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!sessionId || !restaurant) {
    return null;
  }

  const isPremium = user?.is_premium || ['active', 'trialing'].includes(user?.subscription_status);

  return (
    <div className="main-screen">
      <MainHeader title="Confirm Your Visit" showBack={true} />

      <main className="screen-main">
        <div className="visit-confirmation-container">
          {!isPremium && (
            <div className="premium-banner" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              padding: '16px',
              borderRadius: '12px',
              marginBottom: '24px',
              textAlign: 'center',
            }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>💎 Premium Feature</h3>
              <p style={{ margin: '0 0 12px 0', fontSize: '14px', opacity: 0.9 }}>
                Earn $1 cashback on every verified order with Premium
              </p>
              <button
                onClick={() => navigate('/subscription')}
                style={{
                  background: '#fff',
                  color: '#667eea',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Upgrade to Premium
              </button>
            </div>
          )}

          <div className="visit-restaurant-info" style={{
            background: '#f8f9fa',
            padding: '16px',
            borderRadius: '12px',
            marginBottom: '24px',
          }}>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '20px' }}>
              {restaurant.name}
            </h2>
            <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
              {restaurant.address}
            </p>
          </div>

          {isPremium && monthlyRemaining !== null && (
            <div className="cashback-info" style={{
              background: monthlyRemaining > 0 ? '#e8f5e9' : '#fff3e0',
              padding: '16px',
              borderRadius: '12px',
              marginBottom: '24px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                {monthlyRemaining > 0 ? '💰' : '⚠️'}
              </div>
              <h3 style={{ margin: '0 0 4px 0', color: monthlyRemaining > 0 ? '#2e7d32' : '#f57c00' }}>
                {monthlyRemaining > 0 ? 'Earn $1 Cashback Per Order' : 'Monthly Limit Reached'}
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: monthlyRemaining > 0 ? '#558b2f' : '#e65100' }}>
                {monthlyRemaining > 0 
                  ? `${monthlyRemaining} cashback visit${monthlyRemaining === 1 ? '' : 's'} remaining this month`
                  : 'Cashback limit resets next month'}
              </p>
            </div>
          )}

          {error && (
            <div className="error-message" style={{
              background: '#fee',
              color: '#c33',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
            }}>
              {error}
            </div>
          )}

          {/* Order Type Selection */}
          <div className="form-section" style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              🍽️ How did you order?
            </label>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {[
                { value: 'dine_in', label: 'Dine-In', emoji: '🍽️' },
                { value: 'delivery', label: 'Delivery', emoji: '🚗' },
                { value: 'pickup', label: 'Pickup', emoji: '🥡' },
              ].map(type => (
                <button
                  key={type.value}
                  onClick={() => setOrderType(type.value)}
                  disabled={!isPremium}
                  style={{
                    flex: '1 1 auto',
                    padding: '12px 16px',
                    background: orderType === type.value ? '#FF6347' : '#fff',
                    color: orderType === type.value ? '#fff' : '#333',
                    border: `2px solid ${orderType === type.value ? '#FF6347' : '#ddd'}`,
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: isPremium ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                  }}
                >
                  {type.emoji} {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Delivery Platform Selection (if delivery) */}
          {orderType === 'delivery' && (
            <div className="form-section" style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                📱 Which delivery platform?
              </label>
              <select
                value={deliveryPlatform}
                onChange={(e) => setDeliveryPlatform(e.target.value)}
                disabled={!isPremium}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  background: '#fff',
                }}
              >
                <option value="">Select platform...</option>
                <option value="doordash">DoorDash</option>
                <option value="ubereats">Uber Eats</option>
                <option value="grubhub">Grubhub</option>
                <option value="postmates">Postmates</option>
                <option value="seamless">Seamless</option>
                <option value="other">Other</option>
              </select>
            </div>
          )}

          <div className="form-section" style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              📸 Upload Receipt or Food Photo
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              disabled={uploading || !isPremium}
              style={{
                display: 'block',
                width: '100%',
                padding: '12px',
                border: '2px dashed #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            />
            {uploading && <p style={{ marginTop: '8px', color: '#666' }}>Uploading...</p>}
            {photoUrl && (
              <div style={{ marginTop: '12px' }}>
                <img
                  src={photoUrl}
                  alt="Receipt preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    borderRadius: '8px',
                    objectFit: 'contain',
                  }}
                />
              </div>
            )}
          </div>

          <div className="form-section" style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              💵 Order Total
            </label>
            <input
              type="number"
              min="10"
              max="200"
              step="0.01"
              value={billAmount}
              onChange={(e) => setBillAmount(e.target.value)}
              placeholder="Enter total bill amount"
              disabled={!isPremium}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '16px',
              }}
            />
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
              $1 cashback per order · $10 minimum order · up to 10 orders/month
            </p>
          </div>

          <div className="location-check" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            background: locationVerified ? '#e8f5e9' : '#fff3e0',
            borderRadius: '8px',
            marginBottom: '24px',
          }}>
            <span style={{ fontSize: '24px' }}>
              {locationVerified ? '✅' : '📍'}
            </span>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              {locationVerified ? 'Location verified' : 'Verifying location...'}
            </span>
          </div>

          {isPremium && (
            <button
              onClick={handleSubmit}
              disabled={submitting || !photoUrl || !billAmount || !locationVerified}
              style={{
                width: '100%',
                padding: '16px',
                background: submitting || !photoUrl || !billAmount || !locationVerified
                  ? '#ccc'
                  : '#FF6347',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: submitting || !photoUrl || !billAmount || !locationVerified
                  ? 'not-allowed'
                  : 'pointer',
              }}
            >
              {submitting ? 'Verifying...' : 'Confirm Visit & Earn Cashback'}
            </button>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
